import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseSiteService } from '@spartacus/core';
import { Order } from '@spartacus/order/root';
import * as moment from 'moment';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { distinctUntilChanged, pluck, switchMap, take, tap } from 'rxjs/operators';
import { CheckoutConstants } from 'src/app/model/checkout.model';
import { DealerPrice } from 'src/app/model/dealer-price.model';
import { BaseStoreService } from 'src/app/service/base-store/base-store.service';
import { ShimanoBusinessTypeService } from 'src/app/service/businessType/shimano-business-type.service';
import { CheckoutServiceExtends } from 'src/app/service/checkout/checkout.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { CustomTranslationService } from 'src/app/service/language/custom-translation.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { OrderHistoryService } from 'src/app/service/orders/order-history.service';
import { FREIGHT, SnahCountryService } from 'src/app/service/snah/snah-countries.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { getOrderHistoryStatusIcon } from 'src/app/spartacus/features/order/order-history/order-history.component';
import { OrderDetailsService } from '../../../../../service/myaccount/order-details.service';

export interface ShimanoOrder extends Order {
  purchaseOrderNumber?: string;
  createdDateString?: Date;
  placedBy?: string;
  deliverFullOrder?: boolean;
  erpOrderNumber?: string;
  statusCode?: string;
  statusDisplay?: string;
  orderType?: string;
  orderTypeCode?: string;
}
@Component({
  selector: 'app-order-detail-summary',
  templateUrl: './order-detail-summary.component.html',
  styleUrls: ['./order-detail-summary.component.scss'],
  standalone: false,
})
export class OrderDetailSummaryComponent implements OnInit, OnDestroy {
  orderId: string;
  erpOrderType: string;
  order$: Observable<ShimanoOrder>;
  showSrp = false;
  srpSubscription$: Subscription;
  reloadOrderDetailsSubscription$: Subscription;
  dealerPrices: DealerPrice[];
  default = 'default';
  showDeliveryFee: boolean;
  showOrderType: boolean;
  features: any = {};
  baseSite$: Observable<string> = this.baseService.getActive();
  OrderDetailsHideDiscountEnabled: boolean;

  activeCountry$: Observable<string> = this.countryService.getActive();
  activeLanguage$: Observable<string> = this.languageService.getActive();

  isCustomTranslationsEnabled$(customKeys: string[]): Observable<boolean> {
    return combineLatest([this.activeCountry$, this.activeLanguage$]).pipe(
      switchMap(([activeCountry, activeLanguage]) =>
        this.customTranslationService.isCustomTranslationsEnabled$(activeCountry, activeLanguage, customKeys)
      )
    );
  }

  isOrderNumberCustomTranslationsEnabled$: Observable<boolean> = this.isCustomTranslationsEnabled$([
    'myAccount.orderDetails.orderSummary.orderNumber',
  ]);

  isTaxRelatedCustomTranslationsEnabled$: Observable<boolean> = this.isCustomTranslationsEnabled$([
    'myAccount.orderDetails.orderTotalSummary.subtotal',
    'myAccount.orderDetails.orderTotalSummary.tax',
    'myAccount.orderDetails.orderTotalSummary.total',
  ]);

  showDynamicDateFormat$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.DYNAMIC_DATE_FORMAT)
    )
  );

  showSplitDelivery$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.SPLIT_DELIVERY)
    )
  );

  showOrderType$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.ORDER_TYPE)
    )
  );

  showFreeDelivery$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.FREE_DELIVERY)
    )
  );

  dateFormat$: Observable<string> = this.baseStoreService.getBasestoreData().pipe(pluck('dateFormat'));
  showActiveSnahCountry$: Observable<boolean> = this.SnahCountryService.getSnahFeatures(FREIGHT);

  constructor(
    protected orderDetailsService: OrderDetailsService,
    private route: ActivatedRoute,
    private componentService: ComponentService,
    private cdr: ChangeDetectorRef,
    private baseService: BaseSiteService,
    private shimanoBusinessTypeService: ShimanoBusinessTypeService,
    private shimanoCountryService: ShimanoCountryService,
    private languageService: ShimanoLanguageService,
    private countryService: ShimanoCountryService,
    private customTranslationService: CustomTranslationService,
    private checkoutServiceExtends: CheckoutServiceExtends,
    private baseStoreService: BaseStoreService,
    private storageService: StorageService,
    private SnahCountryService: SnahCountryService,
    private orderHistoryService: OrderHistoryService
  ) {
    this.route.params.pipe(take(1)).subscribe((params) => {
      this.orderId = params.orderCode;
    });

    this.route.queryParams.pipe(take(1)).subscribe((data) => {
      this.erpOrderType = data?.erpOrderType;
    });

    this.orderDetailsService
      .getOrderHistoryConfig()
      .pipe(take(1))
      .subscribe((resp: any) => {
        this.showDeliveryFee = resp[this.baseSiteId]
          ? resp[this.baseSiteId].showDeliveryFee
          : resp[this.default].showDeliveryFee;

        this.showOrderType = resp.showOrderType[this.businessType][this.country]
          ? resp.showOrderType[this.businessType][this.country]
          : resp[this.default].showOrderType;

        this.cdr.detectChanges();
      });

    // get customized order history features
    this.orderHistoryService
      .getFeatures()
      .pipe(take(1))
      .subscribe((features: any) => {
        this.features = { ...features, ...this.features };
        this.cdr.detectChanges();
      });
  }
  get baseSiteId(): string {
    let baseSiteId: string;
    this.baseSite$.pipe(take(1)).subscribe((baseSiteData) => (baseSiteId = baseSiteData));
    return baseSiteId;
  }

  get businessType(): string {
    let businessType: string;
    this.shimanoBusinessTypeService
      .getActive()
      .pipe(take(1))
      .subscribe((data) => (businessType = data));
    return businessType;
  }

  get country(): string {
    let country: string;
    this.shimanoCountryService
      .getActive()
      .pipe(take(1))
      .subscribe((data) => (country = data));
    return country;
  }

  ngOnInit() {
    this.order$ = this.languageService.getActive().pipe(
      distinctUntilChanged(),
      switchMap(() => this.orderDetailsService.getOrderDetails(this.orderId, this.erpOrderType)),
      tap((orderDetails) => {
        this.componentService.setOrderDetails(orderDetails);
        this.addFeatures(orderDetails);
      })
    );

    this.reloadOrderDetailsSubscription$ = this.componentService.reloadOrderDetails$
      .pipe(
        switchMap(() => this.orderDetailsService.getOrderDetails(this.orderId, this.erpOrderType)),
        tap((orderDetails) => this.componentService.setOrderDetails(orderDetails))
      )
      .subscribe();

    this.getInitSrpToggleValue();

    this.srpSubscription$ = this.componentService.srpToggle$.subscribe((checked) => {
      this.showSrp = checked;
      this.cdr.detectChanges();
    });
    this.isOrderDetailsHideDiscount();
  }

  getInitSrpToggleValue() {
    this.showSrp = this.storageService.getLocalStorageItem('showSrp') === 'true';
  }

  getStatusIcon(statusCode: string): string {
    return getOrderHistoryStatusIcon(statusCode);
  }

  setCustomContext(isCustomTranslationsEnabled: boolean, activeCountry: string, activeLanguage: string) {
    return isCustomTranslationsEnabled ? [activeCountry, activeLanguage].join('-') : null;
  }

  hasNoFreeDelivery(order: any): string {
    if (order?.deliveryCost === undefined || (order?.deliveryCost?.value === 0 && !order?.freeShipping)) {
      return 'feeInProgress';
    } else if (order?.deliveryCost && order?.deliveryCost?.value > 0 && !order?.freeShipping) {
      return 'shippingFee';
    }
  }

  isOrderDetailsHideDiscount(): void {
    this.orderDetailsService
      .getOrderDetailsHideDiscount$()
      .pipe(take(1))
      .subscribe((enabled) => (this.OrderDetailsHideDiscountEnabled = enabled));
  }

  getTimezoneFromDate(dateString: string): string {
    const date = moment.parseZone(dateString);
    return date.format('Z');
  }

  addFeatures(order: ShimanoOrder): void {
    const orderType = order.orderTypeCode;
    this.features.hideOrderStatus = ['CO', 'SW'].includes(orderType);
    this.features.hideOrderBySplitDeliveryField = ['CO', 'SW'].includes(orderType);
  }

  ngOnDestroy() {
    this.srpSubscription$?.unsubscribe();
    this.reloadOrderDetailsSubscription$?.unsubscribe();
  }
}
